import type { GridColDef } from '@mui/x-data-grid';
import { downloadURL } from '@/common/utils/downloadURL';

export const CSV_ESCAPE_REGEX = /["\n\r,]/;

export function escapeCsvValue(value: string): string {
  if (!CSV_ESCAPE_REGEX.test(value)) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

export function buildCsv<TRow extends object>(
  rows: TRow[],
  cols: GridColDef<TRow>[],
): string {
  const headerRow = cols
    .map((col) => escapeCsvValue(col.headerName ?? col.field))
    .join(',');
  const dataRows = rows.map((row) =>
    cols
      .map((col) => {
        const value = Object.entries(row).find(
          ([key]) => key === col.field,
        )?.[1];
        const isNumber = typeof value === 'number';
        // we need to cast to the specific type of the valueFormatter because GridColDef doesn't have a type for the valueFormatter value param
        const formatter = col.valueFormatter as
          | ((v: unknown, r: TRow, c: GridColDef<TRow>, a: null) => string)
          | undefined;
        const formatted =
          isNumber || formatter == null
            ? value
            : formatter(value, row, col, null);
        return escapeCsvValue(String(formatted ?? ''));
      })
      .join(','),
  );
  return [headerRow, ...dataRows].join('\n');
}

export function downloadCsv(csv: string, filename: string) {
  if (window.median) {
    // Median's native bridge cannot resolve blob: URLs (WebView-scoped).
    // Encode the CSV as a self-contained data: URL instead.
    const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    downloadURL(dataUrl, filename);
  } else {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    downloadURL(url, filename);
    // Revoke on the next macrotask so the download/navigation can read the blob URL first;
    // revoking synchronously can cancel the download (especially in mobile WebViews).
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}
